// ============================================================
// A TRILHA DAS ESCRITAS (issue #593)
//
// O contrato entre a API que grava e lê a trilha (`apps/api/src/lib/audit-*.ts`,
// `routes/audit-log.ts`) e a tela que a mostra (`/settings/audit`, só admin).
//
// ⚠️ `AUDIT_ENTITY_LABELS` é também a lista das tabelas AUDITADAS. O teste de guarda
// da API (`__tests__/audit/cobertura.test.ts`) exige que toda tabela do schema
// esteja aqui ou nas exclusões declaradas da API, com o motivo. Uma tabela nova
// fica vermelha até alguém decidir.
// ============================================================
import { z } from 'zod'

import { PORTAL_MODES, type PortalMode } from './portal-view.ts'

/** O que uma escrita fez. `upsert` é o «cria ou altera» de um `on conflict do update`. */
export const AUDIT_ACTIONS = ['create', 'update', 'upsert', 'delete'] as const
export type AuditAction = (typeof AUDIT_ACTIONS)[number]

export const AUDIT_ACTION_LABELS: Readonly<Record<AuditAction, string>> = {
  create: 'Criou',
  update: 'Alterou',
  upsert: 'Criou ou alterou',
  delete: 'Removeu',
}

/**
 * Como o pedido entrou no portal (issue #584): o usuário do cliente, a Genesis no
 * perfil de cliente, a Genesis como gestora de conta (Story 35.9), ou o owner (Story
 * 35.10). É o `PortalMode` que o `requirePortalUser` decide, e não uma cópia: os dois
 * não divergem. Mais
 * `support`, que já não se grava: a trilha guarda as linhas de antes da 35.9, quando a
 * Genesis entrava em suporte.
 */
export const AUDIT_PORTAL_MODES = [...PORTAL_MODES, 'support'] as const
export type AuditPortalMode = PortalMode | 'support'

export const AUDIT_PORTAL_MODE_LABELS: Readonly<Record<AuditPortalMode, string>> = {
  customer: 'Cliente',
  member: 'Perfil de cliente',
  account_manager: 'Gestor de conta',
  owner: 'Owner',
  support: 'Suporte',
}

/** As tabelas auditadas (o nome FÍSICO) e o nome que a tela mostra. */
export const AUDIT_ENTITY_LABELS: Readonly<Record<string, string>> = {
  accounts: 'Conta de acesso',
  allocation_actions: 'Ação do plano de ação',
  allocation_assignments: 'Atribuição da alocação',
  allocation_reviews: 'Avaliação dos pilares',
  allocations: 'Alocação',
  application_events: 'Evento da candidatura',
  applications: 'Candidatura',
  assessments: 'Modelo de avaliação',
  behavioral_presets: 'Preset comportamental',
  customer_account_managers: 'Gestor da conta do cliente',
  customer_areas: 'Operação do cliente',
  customer_managers: 'Gestor do cliente',
  customers: 'Cliente',
  evaluations: 'Correção',
  genesis_bonds: 'Vínculo com a Genesis',
  genesis_portal_access: 'Acesso da Genesis ao portal',
  interview_questions: 'Pergunta do roteiro',
  interview_topics: 'Tópico do roteiro',
  job_profiles: 'Perfil de vaga',
  list_views: 'Vista salva',
  operation_delivery_managers: 'Delivery Manager da operação',
  operation_managers: 'Gestor do cliente na operação',
  opportunities: 'Vaga',
  opportunity_emails: 'E-mail da vaga',
  opportunity_positions: 'Posição da vaga',
  password_resets: 'Redefinição de senha',
  pipeline_stages: 'Etapa do processo',
  pipelines: 'Processo seletivo',
  portal_access: 'Acesso ao portal',
  portal_access_areas: 'Operação do acesso ao portal',
  position_assignments: 'Responsável da posição',
  position_bill_rates: 'Bill rate da posição',
  positions: 'Posição do cliente',
  professional_note_actions: 'Ação do apontamento',
  professional_note_attachments: 'Anexo de apontamento',
  professional_note_deletions: 'Exclusão de apontamento',
  professional_notes: 'Apontamento',
  professional_roles: 'Função do profissional',
  professional_skills: 'Skill do profissional',
  professionals: 'Profissional',
  public_profiles: 'Perfil público',
  resumes: 'Currículo gerado',
  skill_aliases: 'Sinônimo de skill',
  skills: 'Skill',
  user_invitations: 'Convite',
  users: 'Usuário',
  work_areas: 'Área de atuação',
  work_roles: 'Função',
}

/** O rótulo de uma entidade, ou o nome físico quando a tabela não está no mapa. */
export function auditEntityLabel(entity: string): string {
  return AUDIT_ENTITY_LABELS[entity] ?? entity
}

/** Uma página da trilha. */
export const AUDIT_LOG_PAGE_SIZE = 50

/**
 * Os filtros de `GET /api/audit-log`. O período vem em epoch MILISSEGUNDOS (`from`
 * inclusive, `to` exclusive): a tela converte o dia escolhido no fuso do browser, e
 * o servidor não precisa de saber que fuso é.
 */
export const AuditLogQuerySchema = z.object({
  actorUserId: z.string().min(1).optional(),
  entity: z.string().min(1).optional(),
  /** O id do registro, exato — a busca da tela. */
  recordId: z.string().trim().min(1).optional(),
  from: z.coerce.number().int().nonnegative().optional(),
  to: z.coerce.number().int().nonnegative().optional(),
  /** O `nextCursor` da página anterior. */
  cursor: z.string().min(1).optional(),
})
export type AuditLogQuery = z.infer<typeof AuditLogQuerySchema>

/** O que `detail` traz: nomes de campo, chaves `*_id` e a contagem. Nunca um valor. */
export type AuditLogDetail = {
  fields?: string[]
  keys?: Record<string, string>
  count: number
}

/** Uma linha da trilha, como a tela a recebe. */
export type AuditLogEntry = {
  id: string
  /** ISO 8601. */
  at: string
  actor: {
    kind: 'user' | 'public'
    userId: string | null
    name: string | null
    email: string | null
    role: string | null
  }
  /** Só nas escritas feitas pelo portal. */
  portal: { customerId: string; customerName: string | null; mode: AuditPortalMode } | null
  action: AuditAction
  entity: string
  recordId: string | null
  detail: AuditLogDetail
  method: string
  route: string
}

export type AuditLogPage = { items: AuditLogEntry[]; nextCursor: string | null }

/** Um ator que aparece na trilha, com o nome e o e-mail mais recentes que ela guarda. */
export type AuditLogActor = { userId: string; name: string | null; email: string | null }
